import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/subscription-plans").then((r) => setPlans(r.data || []));
    if (user) api.get("/me/access").then((r) => setMe(r.data)).catch(() => {});
  }, [user]);

  const subscribe = async (plan) => {
    if (!user) {
      toast.error("Sign in to subscribe");
      navigate("/login");
      return;
    }
    setBusy(plan.code);
    try {
      if (plan.code === "free") {
        await api.post("/subscriptions/subscribe", { plan_code: "free", billing });
        toast.success("You're on the Free plan");
        const r = await api.get("/me/access"); setMe(r.data);
      } else {
        // Go straight to checkout; payment side-effect activates subscription
        navigate(`/payments/checkout?context_type=subscription&context_id=${plan.code}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally { setBusy(null); }
  };

  const priceFor = (p) => billing === "annual" ? p.price_npr_annual : p.price_npr_monthly;
  const cadenceLabel = billing === "annual" ? "/year" : "/month";

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="pricing-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="text-center">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SIMPLE PLANS</div>
          <h1 className="font-serif text-4xl lg:text-6xl text-kb-ink mt-2 leading-tight">
            Build your research, <em className="italic text-kb-primary">priced fairly</em>.
          </h1>
          <p className="mt-5 text-kb-ink-muted max-w-2xl mx-auto">
            Free for browsing, Standard for active researchers, Premium for the full library
            — including paper explainers and bundled LMS courses. Switch any time.
          </p>

          <div className="mt-8 inline-flex border border-slate-200 rounded-sm bg-white p-1" data-testid="billing-toggle">
            {[
              { v: "monthly", l: "Monthly" },
              { v: "annual", l: "Annual · save ~17%" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setBilling(opt.v)}
                className={`px-4 py-1.5 rounded-sm text-sm transition-colors ${billing === opt.v ? "bg-kb-primary text-white" : "text-kb-ink-muted hover:text-kb-primary"}`}
                data-testid={`billing-${opt.v}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="pricing-plans-grid">
          {plans.map((p, idx) => {
            const isCurrent = me && me.plan_code === p.code;
            const isFlagship = idx === 1; // middle plan visually flagged
            return (
              <article
                key={p.code}
                className={`kb-card p-6 flex flex-col relative ${isFlagship ? "ring-2 ring-kb-primary shadow-lg" : ""}`}
                data-testid={`plan-card-${p.code}`}
              >
                {isFlagship && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 kb-mono text-[10px] uppercase tracking-[0.2em] bg-kb-primary text-white px-3 py-1 rounded-sm inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> POPULAR
                  </div>
                )}
                <h3 className="font-serif text-2xl text-kb-ink">{p.name}</h3>
                <p className="text-sm text-kb-ink-muted mt-1 min-h-[40px]">{p.description}</p>
                <div className="mt-5">
                  {priceFor(p) > 0 ? (
                    <>
                      <span className="font-serif text-4xl text-kb-ink">NPR {priceFor(p).toLocaleString()}</span>
                      <span className="kb-mono text-xs text-kb-ink-muted ml-1">{cadenceLabel}</span>
                    </>
                  ) : (
                    <span className="font-serif text-4xl text-kb-ink">Free</span>
                  )}
                </div>
                <ul className="mt-6 space-y-2 text-sm text-kb-ink flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-kb-primary shrink-0 mt-0.5" strokeWidth={2.2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(p)}
                  disabled={busy === p.code || isCurrent}
                  className={`mt-6 text-sm justify-center w-full ${isCurrent ? "kb-btn-outline opacity-60 cursor-not-allowed" : isFlagship ? "kb-btn-primary" : "kb-btn-outline"}`}
                  data-testid={`plan-${p.code}-cta`}
                >
                  {isCurrent ? "Current plan" : (busy === p.code ? "…" : p.cta_label || "Choose")} {!isCurrent && <ArrowRight className="w-3.5 h-3.5" />}
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-16 text-center text-xs text-kb-ink-muted">
          All payments are processed securely through eSewa, Khalti or bank transfer. Cancel anytime — your access stays active until the end of the billing period.
        </div>
      </main>
      <Footer />
    </div>
  );
}
